import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Search, Upload, Eye, X, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { COLORS } from '../../utils/constants';

const C = COLORS;
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const StudentList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTeacherOrManager = user?.role === 'manager' || user?.user_type === 'Teacher';

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const fileInputRef = useRef(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/students');
      setStudents(res.data);
    } catch (err) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleDelete = async (id) => {
    const confirmMsg = isTeacherOrManager
      ? 'Are you sure you want to remove this student from your list? (The student will remain in the database for Admin)'
      : 'Are you sure you want to delete this student?';
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await api.delete(`/students/${id}`);
      toast.success(res.data?.message || (isTeacherOrManager ? 'Student removed from your list successfully' : 'Student deleted successfully'));
      fetchStudents();
    } catch (err) {
      toast.error('Failed to delete student');
    }
  };

  const downloadTemplate = () => {
    const csvContent = "UDISE,First Name,Last Name,Title,Gender,Date of Birth,Father Name,Mother Name,Aadhaar Number,Phone Number,Email Address,Address,Board,Class,SRN Number,Status\n07080100101,John,Doe,Mr,Male,2010-06-15,Richard Doe,Jane Doe,123456789012,9876543210,john@example.com,123 Main St,CBSE,10,SRN12345,Active";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "Student_Bulk_Upload_Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredStudents = students.filter(s => {
    const term = search.toLowerCase();
    return (
      (s.first_name || '').toLowerCase().includes(term) ||
      (s.last_name || '').toLowerCase().includes(term) ||
      (s.udise || '').toLowerCase().includes(term) ||
      (s.school_name || '').toLowerCase().includes(term) ||
      (s.email || '').toLowerCase().includes(term) ||
      (s.phone || '').toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <AppLayout title="Students">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ position: 'relative', width: 250 }}>
          <Search size={18} color={C.g400} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search students..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={downloadTemplate}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#fff', color: C.g700, border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            <Download size={18} /> Template
          </button>
          <button
            onClick={() => navigate('/student/bulk-upload')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#fff', color: C.g700, border: `1px solid ${C.g200}`, borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            <Upload size={18} /> Bulk Upload
          </button>
          <button
            onClick={() => navigate('/student/add')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)' }}
          >
            <Plus size={18} /> Add Student
          </button>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 1000 }}>
            <thead>
              <tr style={{ background: C.g50, borderBottom: `1px solid ${C.g200}` }}>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase', width: 60 }}>Photo</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Name</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>School Name</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Contact</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ padding: 40, textAlign: 'center', color: C.g400 }}>Loading...</td></tr>
              ) : paginatedStudents.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: 40, textAlign: 'center', color: C.g500 }}>
                    No students found.
                  </td>
                </tr>
              ) : (
                paginatedStudents.map(s => (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${C.g100}` }}>
                    <td style={{ padding: '12px 20px' }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: C.g100, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {s.profile_photo ? (
                          <img src={`${BASE_URL.replace('/api', '')}${s.profile_photo}`} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ color: C.g400, fontSize: 14, fontWeight: 600 }}>{s.first_name?.charAt(0)}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 14, fontWeight: 500, color: C.g900 }}>
                      {s.title ? `${s.title} ` : ''}{s.first_name} {s.last_name}
                      <div style={{ fontSize: 12, color: C.g500, fontWeight: 400 }}>{s.gender || '-'}</div>
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>
                      <div style={{ fontWeight: 500 }}>{s.school_name || '-'}</div>
                      <div style={{ fontSize: 12, color: C.g400 }}>UDISE: {s.udise || '-'}</div>
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>
                      <div>{s.phone || '-'}</div>
                      <div>{s.email || '-'}</div>
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <button onClick={() => setSelectedStudent(s)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.primary, marginRight: 8 }} title="View Student">
                        <Eye size={16} />
                      </button>
                      <button onClick={() => navigate(`/student/edit/${s.id}`)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: C.g400, marginRight: 8 }} title="Edit Student">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(s.id)} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: '#EF4444' }} title={isTeacherOrManager ? "Remove from my list" : "Delete Student"}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px', borderTop: `1px solid ${C.g100}`, gap: 8 }}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
              style={{ padding: '6px 12px', border: `1px solid ${C.g200}`, background: '#fff', borderRadius: 6, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              Prev
            </button>
            <span style={{ padding: '6px 12px', fontSize: 14, color: C.g600 }}>Page {currentPage} of {totalPages}</span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              style={{ padding: '6px 12px', border: `1px solid ${C.g200}`, background: '#fff', borderRadius: 6, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {selectedStudent && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: `1px solid ${C.g100}`, position: 'sticky', top: 0, background: '#fff', zIndex: 10 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.g900 }}>Student Details</h2>
              <button onClick={() => setSelectedStudent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.g400, padding: 4 }}>
                <X size={20} />
              </button>
            </div>
            
            <div style={{ padding: 24 }}>
              <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
                <div style={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: C.g100, overflow: 'hidden', flexShrink: 0 }}>
                  {selectedStudent.profile_photo ? (
                    <img src={`${BASE_URL.replace('/api', '')}${selectedStudent.profile_photo}`} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.g400, fontSize: 24, fontWeight: 600 }}>
                      {selectedStudent.first_name?.charAt(0)}
                    </div>
                  )}
                </div>
                <div>
                  <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: C.g900 }}>{selectedStudent.title ? `${selectedStudent.title} ` : ''}{selectedStudent.first_name} {selectedStudent.last_name}</h3>
                  <div style={{ fontSize: 13, color: C.primary, fontWeight: 500 }}>
                    {selectedStudent.school_name || 'No School Assigned'} (UDISE: {selectedStudent.udise || 'N/A'})
                  </div>
                  <div style={{ fontSize: 13, color: C.g500, marginTop: 4 }}>
                    {selectedStudent.board ? `Board: ${selectedStudent.board}` : 'No Board'} | {selectedStudent.class ? selectedStudent.class : 'No Class'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, fontWeight: 600, textTransform: 'uppercase' }}>Contact Info</p>
                  <p style={{ margin: 0, fontSize: 14, color: C.g800 }}>📞 {selectedStudent.phone || 'N/A'}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 14, color: C.g800 }}>✉️ {selectedStudent.email || 'N/A'}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, fontWeight: 600, textTransform: 'uppercase' }}>Personal Info</p>
                  <p style={{ margin: 0, fontSize: 14, color: C.g800 }}><strong>Gender:</strong> {selectedStudent.gender || 'N/A'}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 14, color: C.g800 }}><strong>DOB:</strong> {selectedStudent.dob ? selectedStudent.dob.split('T')[0] : 'N/A'}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 14, color: C.g800 }}><strong>Aadhaar:</strong> {selectedStudent.aadhaar || 'N/A'}</p>
                </div>
                
                <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                  <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, fontWeight: 600, textTransform: 'uppercase' }}>Parent Details</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <p style={{ margin: 0, fontSize: 14, color: C.g800 }}><strong>Father's Name:</strong> {selectedStudent.father_name || 'N/A'}</p>
                    <p style={{ margin: 0, fontSize: 14, color: C.g800 }}><strong>Mother's Name:</strong> {selectedStudent.mother_name || 'N/A'}</p>
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                  <p style={{ margin: '0 0 4px', fontSize: 12, color: C.g400, fontWeight: 600, textTransform: 'uppercase' }}>Address</p>
                  <p style={{ margin: 0, fontSize: 14, color: C.g800, lineHeight: 1.5 }}>{selectedStudent.address || 'N/A'}</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.g100}`, background: C.g50, display: 'flex', justifyContent: 'flex-end', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
              <button onClick={() => setSelectedStudent(null)} style={{ padding: '8px 16px', background: '#fff', border: `1px solid ${C.g200}`, borderRadius: 8, cursor: 'pointer', fontWeight: 500, color: C.g700 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </AppLayout>
  );
};

export default StudentList;
